import { config } from '../config/config.js';
import { testimonyApi } from '../services/api.js';
import { 
  uploadInChunks, 
  getResumableUpload, 
  clearUploadState 
} from './chunkUpload.js';

/**
 * Upload video to Google Cloud Storage using signed URL (legacy method)
 * @param {Blob} videoBlob - Video blob to upload
 * @param {string} fileName - File name for the video
 * @param {string} testimonyId - Testimony ID for authorization
 * @param {function} onProgress - Progress callback (percentage)
 * @returns {Promise<string>} - Public URL of uploaded video
 */
export const uploadToGCSLegacy = async (videoBlob, fileName, testimonyId, onProgress = null) => {
  try {
    console.log('📤 Upload starting:', { fileName, testimonyId, size: videoBlob.size });
    
    if (!testimonyId) {
      throw new Error('Testimony ID is required for upload');
    }

    console.log('🔑 Getting signed URL for testimony:', testimonyId);
    
    // Get signed URL from backend
    const { data } = await testimonyApi.getUploadUrl(testimonyId, videoBlob.type || 'video/webm');
    const { uploadUrl, publicUrl } = data;
    
    console.log('✅ Got signed URL, uploading to GCS...');

    // Upload directly to GCS using signed URL
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      if (onProgress) {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentage = Math.round((e.loaded / e.total) * 100);
            onProgress(percentage);
          }
        });
      }

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status: ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload aborted'));
      });

      // Open PUT request to signed URL
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', videoBlob.type || 'video/webm');
      
      // Send the video blob
      xhr.send(videoBlob);
    });

    return publicUrl;
  } catch (error) {
    console.error('GCS upload error:', error);
    throw new Error('Failed to upload video: ' + error.message);
  }
};

/**
 * Upload video with chunking and resume capability
 * @param {Blob} videoBlob - Video blob to upload
 * @param {string} fileName - File name for the video
 * @param {string} testimonyId - Testimony ID for authorization
 * @param {function} onProgress - Progress callback (percentage)
 * @param {object} resumeState - Optional resume state from previous upload attempt
 * @returns {Promise<string>} - Public URL of uploaded video
 */
export const uploadToGCS = async (videoBlob, fileName, testimonyId, onProgress = null, resumeState = null, authToken = null) => {
  try {
    console.log('📤 Chunk upload starting:', { 
      fileName, 
      testimonyId, 
      size: videoBlob.size,
      resuming: !!resumeState 
    });
    
    if (!testimonyId) {
      throw new Error('Testimony ID is required for upload');
    }

    // Use chunk upload with progress callback
    const { publicUrl } = await uploadInChunks(
      videoBlob,
      testimonyId,
      fileName,
      (percentage, currentChunk, totalChunks) => {
        console.log(`📊 Upload progress: ${percentage.toFixed(1)}% (chunk ${currentChunk}/${totalChunks})`);
        if (onProgress) {
          onProgress(percentage);
        }
      },
      resumeState,
      authToken
    );

    console.log('✅ Upload complete:', publicUrl);
    return publicUrl;
  } catch (error) {
    console.error('Chunk upload error:', error);
    throw new Error('Failed to upload video: ' + error.message);
  }
};

/**
 * Check if there's a resumable upload for this testimony
 * @param {string} testimonyId - Testimony ID
 * @returns {object|null} Resume state or null
 */
export const checkResumableUpload = (testimonyId) => {
  return getResumableUpload(testimonyId);
};

/**
 * Clear any saved upload state
 */
export const clearSavedUploadState = () => {
  clearUploadState();
};
